import { useQuery } from '@tanstack/react-query'
import type { IInsight, IQuestionDashboard, IQuestionRecommendation } from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import type { ApiResponse } from '@/api/http/types'
import { QUESTION_AI_FINDINGS_TIMEOUT_MS } from '@/api/http/requestTimeout'
import { useSessionStore } from '@/stores/useSessionStore'

const POLL_MS = 30_000

function tenantKey(tenantId: string | null | undefined) {
  return tenantId ?? '_'
}

function questionDashboardBase(instrumentId: string, questionId: string) {
  return `/api/v1/question-dashboard/${instrumentId}/${questionId}`
}

/** TC-QD-01/02 — fetches the per-question drill-down dashboard (polls every 30s). */
export function useQuestionDashboard(
  instrumentId: string,
  questionId: string,
  options?: { enabled?: boolean },
) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.questionDashboard.detail(
      tenantKey(tenantId),
      instrumentId,
      questionId,
    ),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<IQuestionDashboard>>(
        questionDashboardBase(instrumentId, questionId),
      )
      if (data.data === undefined) {
        throw new Error('Question dashboard response missing data payload')
      }
      return data.data
    },
    enabled:
      (options?.enabled ?? true) &&
      isAuthenticated &&
      !!instrumentId &&
      !!questionId,
    refetchInterval: (query) =>
      query.state.data?.textAnalytics?.aiSource === 'pending' ? 5_000 : POLL_MS,
  })
}

/** TC-QD-30 — cross-tab insight cards for a question (may trigger on-demand AI generation). */
export function useQuestionInsights(
  instrumentId: string,
  questionId: string,
  enabled = true,
) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.insights.question(
      tenantKey(tenantId),
      instrumentId,
      questionId,
    ),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<IInsight[]>>(
        `${questionDashboardBase(instrumentId, questionId)}/insights`,
        { timeout: QUESTION_AI_FINDINGS_TIMEOUT_MS },
      )
      const rows = data.data
      return Array.isArray(rows) ? rows : []
    },
    enabled: enabled && isAuthenticated && !!instrumentId && !!questionId,
    refetchInterval: (query) =>
      enabled && (query.state.data?.length ?? 0) === 0 ? 5_000 : POLL_MS,
  })
}

/** TC-QD-32 — AI interpretation for a question (loads after insights exist). */
export function useQuestionRecommendation(
  instrumentId: string,
  questionId: string,
  enabled = true,
  insightsRevision = '',
) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)

  return useQuery({
    queryKey: queryKeys.insights.questionRecommendation(
      tenantKey(tenantId),
      instrumentId,
      questionId,
      insightsRevision,
    ),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<IQuestionRecommendation | null>>(
        `${questionDashboardBase(instrumentId, questionId)}/recommendation`,
        { timeout: QUESTION_AI_FINDINGS_TIMEOUT_MS },
      )
      return data.data
    },
    enabled: enabled && isAuthenticated && !!instrumentId && !!questionId,
    refetchInterval: (query) =>
      enabled && query.state.data == null ? 5_000 : POLL_MS,
  })
}
